import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { register, user } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
      return;
    }
    if (!token) {
      toast.error('No invite token provided');
      navigate('/register');
      return;
    }
    fetchInvite();
  }, [token, user, navigate]);

  const fetchInvite = async () => {
    try {
      const response = await axios.get(`${API}/invite/${token}`);
      setInvite(response.data);
    } catch (error) {
      toast.error('Invalid or expired invite link');
      navigate('/register');
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register(invite.email, password, name, token);
      toast.success(`Welcome to ${invite.family_name}!`);
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail) || error.message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvite) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-teal-600 font-medium">Loading invite...</div>
      </div>
    );
  }

  if (!invite) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-4" data-testid="join-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Kinsy" className="h-10 mx-auto mb-6" />
        </div>

        {/* Invite Card */}
        <div className="card-soft mb-8 text-center">
          <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">
            You're invited!
          </h1>
          <p className="text-stone-500">
            Join <span className="font-medium text-stone-900">{invite.family_name}</span> on Kinsy
          </p>
        </div>

        {/* Form */}
        <div className="card-soft">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={invite.email}
                disabled
                className="h-12 rounded-xl border-stone-200 bg-stone-50"
                data-testid="join-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-stone-700">Your Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                required
                data-testid="join-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-700">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 pr-10"
                  required
                  data-testid="join-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium text-base"
              data-testid="join-submit-btn"
            >
              {loading ? 'Joining...' : 'Join Family'}
            </Button>
          </form>
        </div>

        {/* Login Link */}
        <p className="mt-6 text-center text-stone-500">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
